import { type RefCallback, useCallback, useRef } from 'react'

/**
 * Callback ref with cleanup support.
 * React 19 supports this natively, but some libraries do not respect
 * the new way of handling callback refs. This will normalize it.
 *
 * @param onConnect Called with the element. Can return a disposer.
 * @returns A callback ref.
 */
export function useCallbackRef<T extends Element>(
  onConnect?: (element: T) => (() => void) | void,
): RefCallback<T> {
  const cleanupRef = useRef<(() => void) | void>(undefined)

  return useCallback(
    (element: T | null) => {
      // This is the legacy path for when a component is unmounted.
      // It's called with `null`.
      if (element === null) {
        cleanupRef.current?.()
        cleanupRef.current = undefined
        return
      }

      // This is for React 19, which will call the returned function.
      const cleanup = onConnect?.(element)
      cleanupRef.current = cleanup
      return () => {
        cleanup?.()
      }
    },
    [onConnect],
  )
}
